import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Constants for time bounds
const MIN_TIME = new Date('2025-05-01T04:00:00.000Z');

interface UseTimeNavigationResult {
  // Time change functions
  handleTimeChange: (hours: number) => void;
  handleDateTimePickerChange: (newValue: dayjs.Dayjs | null) => void;
  resetToNow: () => void;

  // Helper functions
  getDatePickerValue: () => dayjs.Dayjs;
  getMaxTime: () => Date;

  // Computed values
  currentTime: Date;
  isAtMinTime: boolean;
  isAtMaxTime: boolean;
  canGoBack: boolean;
  canGoForward: boolean;
}

/**
 * Custom hook for time navigation logic
 * Consolidates all time-related operations from TimeNavigationControls
 *
 * @param currentTime - Current selected time (null means use current time)
 * @param onTimeChange - Callback when time changes
 * @param useUTCTime - Whether to display time in UTC or local timezone
 * @returns Time navigation functions and computed values
 */
export function useTimeNavigation(
  currentTime: Date | null,
  onTimeChange: (newTime: Date | null) => void,
  useUTCTime: boolean = true
): UseTimeNavigationResult {
  // Use current time if null
  const now = currentTime || new Date();

  // Always get the latest UTC time as max
  const getMaxTime = useCallback(() => new Date(), []);

  // Get the correct dayjs value for DateTimePicker based on timezone mode
  const getDatePickerValue = useCallback(() => {
    // Ensure we always have a valid date
    const dateToUse = now || new Date();

    // The 'now' date is always stored in UTC
    if (useUTCTime) {
      // For UTC display, use dayjs.utc() to ensure it's treated as UTC
      return dayjs.utc(dateToUse);
    } else {
      // For local display, use regular dayjs() which will show local time
      return dayjs(dateToUse);
    }
  }, [now, useUTCTime]);

  // Handle date/time picker change
  const handleDateTimePickerChange = useCallback((newValue: dayjs.Dayjs | null) => {
    if (newValue) {
      // Always store in UTC internally
      let newTime: Date;
      if (useUTCTime) {
        // If displaying UTC, the value is already in UTC
        newTime = newValue.utc().toDate();
      } else {
        // If displaying local time, convert from local to UTC for storage
        newTime = newValue.toDate();
      }

      // Apply same constraints as button handlers
      const MAX_TIME = getMaxTime();
      let clampedTime = newTime;
      if (newTime < MIN_TIME) {
        clampedTime = new Date(MIN_TIME);
      } else if (newTime > MAX_TIME) {
        clampedTime = new Date(MAX_TIME);
      }
      onTimeChange(clampedTime);
    }
  }, [useUTCTime, onTimeChange, getMaxTime]);

  // Handle time increment/decrement
  const handleTimeChange = useCallback((hours: number) => {
    const MAX_TIME = getMaxTime();
    let newTime = new Date(now.getTime() + hours * 60 * 60 * 1000);

    // Clamp to bounds
    if (newTime < MIN_TIME) {
      newTime = new Date(MIN_TIME);
    } else if (newTime > MAX_TIME) {
      newTime = new Date(MAX_TIME);
    }
    onTimeChange(newTime);
  }, [now, onTimeChange, getMaxTime]);

  // Reset to current time
  const resetToNow = useCallback(() => {
    onTimeChange(null); // null means use current time
  }, [onTimeChange]);

  // Computed values for navigation state
  const isAtMinTime = useMemo(() => {
    return now.getTime() <= MIN_TIME.getTime();
  }, [now]);

  const isAtMaxTime = useMemo(() => {
    const maxTime = getMaxTime();
    return now.getTime() >= maxTime.getTime();
  }, [now, getMaxTime]);

  const canGoBack = useMemo(() => {
    return now.getTime() > MIN_TIME.getTime();
  }, [now]);

  const canGoForward = useMemo(() => {
    const maxTime = getMaxTime();
    return now.getTime() < maxTime.getTime();
  }, [now, getMaxTime]);

  return {
    // Time change functions
    handleTimeChange,
    handleDateTimePickerChange,
    resetToNow,

    // Helper functions
    getDatePickerValue,
    getMaxTime,

    // Computed values
    currentTime: now,
    isAtMinTime,
    isAtMaxTime,
    canGoBack,
    canGoForward
  };
}

// Export the MIN_TIME constant for components that need it
export { MIN_TIME };